import NativePushBridge from './NativePushBridge';
import { SystemBars, SystemBarsStyle } from '@capacitor/core';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { supabase } from '../lib/supabase';
import { getAppLinkPath, isNativeApp } from '../lib/platform';
import { openExternalUrl } from '../lib/nativeBrowser';
import { dismissNativeOverlay } from '../hooks/useNativeBack';
import { useToast } from './ToastProvider';

export default function NativeAppBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const [offline, setOffline] = useState(!navigator.onLine);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let disposed = false;
    let keyboardVisible = false;
    let lastLink = '';
    let lastLinkAt = 0;
    const listeners = [];
    const listen = async (plugin, event, callback) => {
      const handle = await plugin.addListener(event, callback);
      if (disposed) await handle.remove();
      else listeners.push(handle);
    };
    const resume = () => {
      window.dispatchEvent(new Event('focus'));
      window.dispatchEvent(new Event('bb:settlements-changed'));
      window.dispatchEvent(new Event('bb:native-resume'));
    };
    const handleLink = async (rawUrl) => {
      const path = getAppLinkPath(rawUrl);
      if (!path || (rawUrl === lastLink && Date.now() - lastLinkAt < 1500)) return;
      lastLink = rawUrl;
      lastLinkAt = Date.now();
      try {
        const url = new URL(rawUrl);
        const hash = new URLSearchParams(url.hash.slice(1));
        if (hash.has('access_token') && hash.has('refresh_token')) {
          const { error } = await supabase.auth.setSession({
            access_token: hash.get('access_token'),
            refresh_token: hash.get('refresh_token'),
          });
          if (error) throw error;
        } else if (url.searchParams.has('code') && ['/confirmed', '/reset-password'].includes(url.pathname)) {
          const { error } = await supabase.auth.exchangeCodeForSession(url.searchParams.get('code'));
          if (error) throw error;
        }
        if (disposed) return;
        await Browser.close().catch(() => {});
        // Keep recovery context, but never put bearer tokens into router history.
        url.searchParams.delete('code');
        if (hash.get('type') === 'recovery') url.searchParams.set('recovery', '1');
        navigate(`${url.pathname}${url.search}${hash.has('access_token') ? '' : url.hash}`, { replace: true });
        resume();
      } catch {
        toastRef.current?.show('Deze link kon niet worden geopend. Probeer opnieuw of vraag een nieuwe inloglink aan.', { type: 'error' });
      }
    };
    const back = async () => {
      if (keyboardVisible) {
        await Keyboard.hide().catch(() => {});
        return;
      }
      if (dismissNativeOverlay()) return;
      const rootScreens = ['/app', '/input', '/leaderboard', '/log', '/login', '/onboarding'];
      if (rootScreens.includes(locationRef.current.pathname)) await App.minimizeApp();
      else if (window.history.state?.idx > 0) navigate(-1);
      else navigate('/input', { replace: true });
    };
    const updateBars = () => {
      const dark = document.documentElement.classList.contains('dark');
      SystemBars.setStyle({ style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light }).catch(() => {});
      StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: dark ? '#09090b' : '#ffffff' }).catch(() => {});
    };
    const observer = new MutationObserver(updateBars);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    updateBars();
    const externalClick = (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || event.defaultPrevented || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) return;
      event.preventDefault();
      // Turnstile is hosted on the public website; keep contact there.
      const path = getAppLinkPath(url.toString());
      if (path) handleLink(url.toString());
      else openExternalUrl(url.toString()).catch(() => {
        toastRef.current?.show('Deze link kon niet worden geopend.', { type: 'error' });
      });
    };
    const connectivity = () => setOffline(!navigator.onLine);
    const browserClosed = () => resume();
    document.addEventListener('click', externalClick);
    window.addEventListener('online', connectivity);
    window.addEventListener('offline', connectivity);
    const start = async () => {
      await Promise.all([
        listen(App, 'appUrlOpen', ({ url }) => handleLink(url)),
        listen(App, 'backButton', back),
        listen(App, 'appStateChange', ({ isActive }) => {
          if (isActive) { supabase.auth.startAutoRefresh(); resume(); }
          else supabase.auth.stopAutoRefresh();
        }),
        listen(Browser, 'browserFinished', browserClosed),
        listen(Keyboard, 'keyboardWillShow', () => {
          keyboardVisible = true;
          document.documentElement.classList.add('bb-keyboard-open');
          requestAnimationFrame(() => document.activeElement?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }));
        }),
        listen(Keyboard, 'keyboardWillHide', () => {
          keyboardVisible = false;
          document.documentElement.classList.remove('bb-keyboard-open');
        }),
      ]);
      if (disposed) return;
      const launch = await App.getLaunchUrl();
      if (launch?.url) await handleLink(launch.url);
      supabase.auth.startAutoRefresh();
    };
    start().catch(() => {});
    // Native splash hands off to the React loading screen, including on a slow network.
    SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    return () => {
      disposed = true;
      listeners.forEach((handle) => handle.remove());
      observer.disconnect();
      document.removeEventListener('click', externalClick);
      window.removeEventListener('online', connectivity);
      window.removeEventListener('offline', connectivity);
      document.documentElement.classList.remove('bb-keyboard-open');
    };
  }, [navigate]);

  if (!isNativeApp()) return null;
  return <>
    <NativePushBridge />
    {offline && <div className="bb-offline-banner" role="status">Je bent offline. Maak verbinding om wijzigingen op te slaan.</div>}
  </>;
}
